import { useState } from 'react';
import { Shield, Mail, Palette, Bell } from 'lucide-react';

export default function Settings() {
  const [settings, setSettings] = useState({
    requireMFA: true,
    sessionTimeout: 30,
    ipWhitelist: '',
    emailOnNewUser: true,
    emailOnHighUsage: true,
    emailDailySummary: false,
    primaryColor: '#0066FF',
    companyName: 'Cyrenza Inc.',
  });

  const handleSave = () => {
    alert('Settings saved successfully!');
    console.log('[SETTINGS] Saved:', settings);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-normal tracking-tight text-gray-900 mb-1">
          Settings
        </h1>
        <p className="text-sm text-gray-600 font-normal tracking-tight">
          Configure admin panel preferences
        </p>
      </div>

      {/* Security */}
      <div className="admin-card">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-normal tracking-tight text-gray-900">
            Security
          </h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-normal tracking-tight text-gray-900">
                Require MFA for admins
              </p>
              <p className="text-xs text-gray-600">Two-factor authentication required</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.requireMFA}
                onChange={(e) => setSettings({ ...settings, requireMFA: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-normal tracking-tight text-gray-900 mb-2">
              Session timeout (minutes)
            </label>
            <input
              type="number"
              value={settings.sessionTimeout}
              onChange={(e) => setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) })}
              className="admin-input"
            />
          </div>

          <div>
            <label className="block text-sm font-normal tracking-tight text-gray-900 mb-2">
              IP Whitelist
            </label>
            <input
              type="text"
              value={settings.ipWhitelist}
              onChange={(e) => setSettings({ ...settings, ipWhitelist: e.target.value })}
              className="admin-input"
              placeholder="192.168.1.0/24, 10.0.0.0/8"
            />
            <p className="text-xs text-gray-600 mt-1">Comma-separated IP ranges (leave empty to allow all)</p>
          </div>
        </div>
      </div>

      {/* Email Notifications */}
      <div className="admin-card">
        <div className="flex items-center gap-2 mb-6">
          <Bell className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-normal tracking-tight text-gray-900">
            Notifications
          </h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-normal tracking-tight text-gray-900">
                New user created
              </p>
              <p className="text-xs text-gray-600">Get notified when accounts are created</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.emailOnNewUser}
                onChange={(e) => setSettings({ ...settings, emailOnNewUser: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-normal tracking-tight text-gray-900">
                High API usage alert
              </p>
              <p className="text-xs text-gray-600">Alert on unusual usage spikes</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.emailOnHighUsage}
                onChange={(e) => setSettings({ ...settings, emailOnHighUsage: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-normal tracking-tight text-gray-900">
                Daily summary
              </p>
              <p className="text-xs text-gray-600">Receive daily platform summary</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.emailDailySummary}
                onChange={(e) => setSettings({ ...settings, emailDailySummary: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Branding */}
      <div className="admin-card">
        <div className="flex items-center gap-2 mb-6">
          <Palette className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-normal tracking-tight text-gray-900">
            Branding
          </h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal tracking-tight text-gray-900 mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={settings.companyName}
              onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
              className="admin-input"
            />
          </div>

          <div>
            <label className="block text-sm font-normal tracking-tight text-gray-900 mb-2">
              Primary Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={settings.primaryColor}
                onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                className="h-10 w-20 border border-gray-200 cursor-pointer"
              />
              <input
                type="text"
                value={settings.primaryColor}
                onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                className="admin-input flex-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button onClick={handleSave} className="admin-button">
          Save Settings
        </button>
      </div>
    </div>
  );
}
